import { createAsyncThunk } from '@reduxjs/toolkit';
import { Vector3, Vector3Tuple } from 'three';

import { CONSTANTS } from '@/config/constants';
import type { RootState } from '@/stores/store';

import { BBoxDescription } from '../../types/bboxes';
import { detailBbox } from '../../utils/bbox';

import { selectBboxDescription } from './bboxes.selectors';

export const load3DModel = createAsyncThunk<
  {
    view?: { offset: Vector3Tuple };
    bbox: BBoxDescription;
    baseName: string | '$root';
    type: 'geometryClass' | 'mesh';
  } | null,
  {
    bbox: [Vector3Tuple, Vector3Tuple];
    baseName: string | '$root';
    type: 'geometryClass' | 'mesh';
  }
>('bboxes/load3DModel', async (action, { getState }) => {
  // root mesh will be already scaled down
  const absolute = detailBbox(action.bbox, CONSTANTS.scene.scaleFactor);
  const state = getState() as RootState;
  // avoid recalculating if offset already calculated
  const currentOffset = selectBboxDescription(state, '$root');
  if (currentOffset && currentOffset.bbox) {
    return null;
  }

  if (action.baseName === '$root') {
    // move the mesh up by entire mesh height, move right by half width
    let offset = new Vector3(0, 0, 0);

    if (CONSTANTS.scene.centerByTopRight) {
      offset = new Vector3(-absolute.size.x / 2, 0, absolute.size.z);
    }

    return {
      type: action.type,
      baseName: action.baseName,
      bbox: {
        center: absolute.center.toArray(),
        bbox: [absolute.bbox.min.toArray(), absolute.bbox.max.toArray()],
        radius: absolute.sphere.radius,
        size: absolute.size.toArray(),
      },
      view: {
        offset: offset.toArray(),
      },
    };
  }

  return {
    type: action.type,
    baseName: action.baseName,
    bbox: {
      center: absolute.center.toArray(),
      bbox: [absolute.bbox.min.toArray(), absolute.bbox.max.toArray()],
      radius: absolute.sphere.radius,
      size: absolute.size.toArray(),
    },
  };
});
